import { isNotNil } from 'es-toolkit';

import { type TeacherSchoolInfo } from '@/api';
import { type FilterNode } from '@/module/public/hooks';
import { type SchoolType } from '@/views/build/type';

export class SchoolMapper {
  static COMMON_ID = 9;

  static schoolMap: Map<number, SchoolType> = new Map([
    [0, 'kindergarten'],
    [1, 'primary'],
    [2, 'middle'],
    [3, 'high'],
    [6, 'college'],
    [this.COMMON_ID, 'other'],
  ]);

  static reverseMap: Map<string, number> = new Map(
    Array.from(SchoolMapper.schoolMap.entries()).map(([key, value]) => [
      value,
      key,
    ]),
  );

  static getSchoolTypeNameById(id: number): SchoolType {
    return this.schoolMap.get(id) || 'other';
  }

  static getSchoolTypeNumberByName(name: string): number {
    return this.reverseMap.get(name?.toLowerCase()) || 0;
  }

  static getSafeSchoolType(type?: string | null): SchoolType {
    if (!isNotNil(type)) return 'other';
    if (type.toLowerCase() === 'elementary') return 'primary';
    if (type === 'university' || type === 'graduate') return 'college';

    if (!this.reverseMap.get(type.toLowerCase())) {
      return 'other';
    }
    return type as SchoolType;
  }

  static isEtc(type?: string | number | null): boolean {
    if (!isNotNil(type)) return false;
    if (typeof type === 'number') {
      return this.getSchoolTypeNameById(type) === 'other';
    }
    return type.toLowerCase() === 'other';
  }
}

export const AllSchoolType: FilterNode = {
  ancestors: [],
  categoryTreeId: '',
  categoryGroupId: '',
  type: 'SCHOOL_TYPE',
  name: 'ALL',
  value: 'ALL',
  children: [],
  order: 0,
};

/**
 * 서버 레거시 응답(schoolType, schoolGrade)을
 * 클라이언트 표준 형식(schoolTypeCode, schoolGradeCode)으로 변환하는 어댑터
 *
 * 서버 API가 두 가지 형식을 혼용하여 반환하는 경우를 대응:
 * - 신규: schoolTypeCode, schoolGradeCode (string)
 * - 레거시: schoolType, schoolGrade (string/number)
 */
export function mapTeacherSchoolInfo(
  info?: TeacherSchoolInfo | null,
): { schoolGradeCode?: string; schoolTypeCode?: string } | undefined {
  if (!info) return undefined;

  // 이미 변환된 형식이면 그대로 사용
  if (info.schoolGradeCode || info.schoolTypeCode) {
    return {
      schoolGradeCode: info.schoolGradeCode,
      schoolTypeCode: info.schoolTypeCode,
    };
  }

  // schoolType이 없으면 undefined
  if (!info.schoolType) return undefined;

  // 레거시 형식을 표준 형식으로 변환
  const schoolTypeCode = SchoolMapper.getSafeSchoolType(
    info.schoolType,
  ).toUpperCase();

  // schoolGrade는 선택적 (학년은 입력하지 않을 수 있음)
  const schoolGradeCode = info.schoolGrade
    ? (info.schoolGrade < 10 ? '0' : '') + String(info.schoolGrade)
    : undefined;

  return {
    schoolGradeCode,
    schoolTypeCode,
  };
}
